import { computed } from 'vue';
import type { Composer } from 'vue-i18n';
import { useI18n } from 'vue-i18n';
import type { RouteMeta, RouteParams } from 'vue-router';
import { useRoute } from 'vue-router';

import { useApiClient } from '@/features/api-client/composables/use-api-client';
import type { BreadcrumbModel } from '@/shared/layouts/base/models';

type BreadcrumbEntry = (i18n: Composer, params: RouteParams) => BreadcrumbModel;

type Entry = BreadcrumbEntry | BreadcrumbModel;

type MetaBreadcrumb = RouteMeta & {
  [key]?: BreadcrumbEntry;
};

const key = 'breadcrumb-meta';

export function useBreadcrumbMeta(entry: Entry): MetaBreadcrumb {
  return {
    [key]: (i18n: Composer, params: RouteParams) => {
      return entry instanceof Function ? entry(i18n, params) : entry;
    },
  };
}

function updateBreadcrumbLabel(breadcrumb: BreadcrumbModel, isLoading: boolean, name?: string) {
  if (isLoading) {
    breadcrumb.isLoading = true;
  } else if (name) {
    breadcrumb.label = name;
    breadcrumb.isLoading = false;
  }
}

export function useRouterBreadcrumb() {
  const route = useRoute();
  const i18n = useI18n();

  const apiClientId = computed(() => route.params.id as string);

  const { data: apiClient, isLoading: isApiClientLoading } = useApiClient(apiClientId);

  const breadcrumbs = computed(() => {
    if (route.meta.hideBreadcrumb) {
      return [];
    }

    return route.matched
      .filter(({ meta }) => !!meta[key])
      .map(({ meta }) => (meta[key] as BreadcrumbEntry)(i18n, route.params))
      .map((breadcrumb: BreadcrumbModel) => {
        if (breadcrumb.params?.apiClientId) {
          updateBreadcrumbLabel(breadcrumb, isApiClientLoading.value, apiClient?.value?.name);
        }

        return breadcrumb;
      });
  });

  const showBreadcrumbs = computed(() => {
    return !!breadcrumbs.value.length;
  });

  return { breadcrumbs, showBreadcrumbs };
}
